import React, { createContext, useContext, useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"

export interface Guia {
  _id?: string
  numero: string
  nomeFalecido: string
  nomeMae: string
  nomePai: string
  naturalidade: string
  cor: string
  idade: string
  dataNascimento?: string
  causaMorte: string
  localObito: string
  dataObito: string
  horaObito: string
  obitoNumero: string
  fls: string
  livroNumero: string
  cartorio: string
  dataSepultamento: string
  cemiterio: string
  valorPago: string
  talaoNumero: string
  nomeSolicitante: string
  cpfSolicitante: string
  certidaoFile: string
  certidaoBase64: string
  status: string
  deletedAt?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface Usuario {
  _id?: string
  nomeCompleto: string
  cpf: string
  matricula: string
  email: string
  telefone: string
  username: string
  password: string
  role: "admin" | "user"
  creator: string
  createdAt: string
  updatedAt: string
}

export interface DadosPrefeitura {
  _id?: string
  nomePrefeitura: string
  secretaria: string
  departamento: string
  endereco: string
  cep: string
  cnpj: string
  telefone: string
  chavePix: string
  logoUrl: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface Pagamento {
  _id?: string
  nomePagador: string
  cpfPagador: string
  referenciaPagamento: string
  nomeRecebedor: string
  cnpjRecebedor: string
  chavePix: string
  valor: string
  numeroComprovante: string
  horasValidade: number
  dataExpiracao: string
  statusPagamento: "pendente" | "pago" | "sem_comprovante"
  dataPagamento?: string
  isNFSe?: boolean
  numeroNotaFiscal?: string
  isTLL?: boolean
  valorTLL?: string
  isTFF?: boolean
  valorTFF?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface Aforamento {
  _id?: string
  numero: string
  proprietario: string
  cpfProprietario: string
  denominacao: string
  regiao: string
  valorVenal: string
  ano: string
  impostoAPagar: string
  numeroTalao: string
  dataPagamento: string
  observacao: string
  status: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface Feirante {
  _id?: string
  nomeCompleto: string
  cpf: string
  endereco: string
  uf: string
  contato: string
  valorSemanal: string
  enderecoLote: string
  numeroLote: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface Vendedor {
  _id?: string
  nomeCompleto: string
  cpfCnpj: string
  endereco: string
  telefone: string
  ramosAtividade: string[]
  valor: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface Alerta {
  _id?: string
  feiranteNome: string
  feiranteTelefone: string
  dataReferencia: string
  diaSemana: string
  mensagem: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface Boleto {
  _id?: string
  numeroBoleto: string
  nomePagador: string
  cpfCnpjPagador: string
  enderecoPagador: string
  descricao: string
  valor: string
  dataVencimento: string
  dataEmissao: string
  nossoNumero: string
  linhaDigitavel: string
  codigoBarras: string
  statusBoleto: "pendente" | "pago" | "vencido" | "cancelado"
  dataPagamento?: string
  valorPago?: string
  // Campos para integração futura com API do banco
  convenio?: string
  carteira?: string
  variacaoCarteira?: string
  agencia?: string
  conta?: string
  numeroDocumento?: string
  juros?: string
  multa?: string
  desconto?: string
  instrucoes?: string
  observacoes?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface Empresa {
  _id?: string
  razaoSocial: string
  nomeFantasia?: string
  cnpj: string
  cpf?: string
  cpfResponsavel?: string
  responsavel?: string
  tipoEmpresa?: string
  ramoAtividade?: string
  cnae?: string
  inscricaoEstadual?: string
  inscricaoMunicipal?: string
  endereco?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  uf?: string
  cep?: string
  telefone?: string
  email?: string
  areaM2?: string
  status?: string
  observacoes?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface Alvara {
  _id?: string
  empresaId: string
  numeroAlvara: string
  razaoSocial: string
  nomeFantasia?: string
  cnpj?: string
  tipoEmpresa?: string
  ramoAtividade?: string
  cnae?: string
  inscricaoMunicipal?: string
  responsavel?: string
  cpfResponsavel?: string
  endereco?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  uf?: string
  cep?: string
  telefone?: string
  areaM2?: string
  dataEmissao?: string
  dataValidade?: string
  anoExercicio?: string
  taxaAlvara?: string
  statusAlvara?: string
  observacoes?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface Cemiterio {
  _id?: string
  nome: string
  codigo: string
  endereco: string
  latitude?: string
  longitude?: string
  creator: string
  createdAt: string
  updatedAt: string
}

interface GuiasContextType {
  guias: Guia[]
  guiasExcluidas: Guia[]
  addGuia: (guia: Omit<Guia, "_id" | "status" | "creator" | "createdAt" | "updatedAt">) => Promise<void>
  updateGuia: (id: string, guia: Partial<Guia>) => Promise<void>
  deleteGuia: (id: string) => Promise<void>
  restaurarGuia: (id: string) => Promise<void>
  deletarPermanentemente: (id: string) => Promise<void>
  getGuiaById: (id: string) => Guia | undefined
  fetchGuias: () => Promise<void>
  fetchGuiasExcluidas: () => Promise<void>
  logoUrl: string
  setLogoUrl: (url: string) => Promise<void>
  usuarios: Usuario[]
  addUsuario: (usuario: Omit<Usuario, "_id" | "creator" | "createdAt" | "updatedAt">) => Promise<void>
  updateUsuario: (id: string, usuario: Partial<Usuario>) => Promise<void>
  deleteUsuario: (id: string) => Promise<void>
  getUsuarioById: (id: string) => Usuario | undefined
  fetchUsuarios: () => Promise<void>
  validateLogin: (username: string, password: string) => { success: boolean; user?: Usuario }
  dadosPrefeitura: DadosPrefeitura | null
  setDadosPrefeitura: (dados: Partial<DadosPrefeitura>) => Promise<void>
  fetchConfig: () => Promise<void>
  pagamentos: Pagamento[]
  addPagamento: (pagamento: Omit<Pagamento, "_id" | "numeroComprovante" | "dataExpiracao" | "statusPagamento" | "dataPagamento" | "creator" | "createdAt" | "updatedAt">) => Promise<void>
  marcarComoPago: (id: string, dataPagamentoRetroativa?: string) => Promise<void>
  marcarComoSemComprovante: (id: string) => Promise<void>
  deletePagamento: (id: string) => Promise<void>
  fetchPagamentos: () => Promise<void>
  aforamentos: Aforamento[]
  addAforamento: (aforamento: Omit<Aforamento, "_id" | "status" | "creator" | "createdAt" | "updatedAt">) => Promise<void>
  updateAforamento: (id: string, aforamento: Partial<Aforamento>) => Promise<void>
  deleteAforamento: (id: string) => Promise<void>
  getAforamentoById: (id: string) => Aforamento | undefined
  fetchAforamentos: () => Promise<void>
  feirantes: Feirante[]
  addFeirante: (feirante: Omit<Feirante, "_id" | "creator" | "createdAt" | "updatedAt">) => Promise<void>
  updateFeirante: (id: string, feirante: Partial<Feirante>) => Promise<void>
  deleteFeirante: (id: string) => Promise<void>
  getFeiranteById: (id: string) => Feirante | undefined
  fetchFeirantes: () => Promise<void>
  alertas: Alerta[]
  addAlerta: (alerta: Omit<Alerta, "_id" | "creator" | "createdAt" | "updatedAt">) => Promise<void>
  fetchAlertas: () => Promise<void>
  boletos: Boleto[]
  addBoleto: (boleto: Omit<Boleto, "_id" | "numeroBoleto" | "nossoNumero" | "linhaDigitavel" | "codigoBarras" | "statusBoleto" | "creator" | "createdAt" | "updatedAt">) => Promise<void>
  updateBoleto: (id: string, boleto: Partial<Boleto>) => Promise<void>
  deleteBoleto: (id: string) => Promise<void>
  cancelarBoleto: (id: string) => Promise<void>
  marcarBoletoComoPago: (id: string, valorPago: string) => Promise<void>
  getBoletoById: (id: string) => Boleto | undefined
  fetchBoletos: () => Promise<void>
  vendedores: Vendedor[]
  addVendedor: (vendedor: Omit<Vendedor, "_id" | "creator" | "createdAt" | "updatedAt">) => Promise<void>
  updateVendedor: (id: string, vendedor: Partial<Vendedor>) => Promise<void>
  deleteVendedor: (id: string) => Promise<void>
  getVendedorById: (id: string) => Vendedor | undefined
  fetchVendedores: () => Promise<void>
  cemiterios: Cemiterio[]
  addCemiterio: (cemiterio: Omit<Cemiterio, "_id" | "creator" | "createdAt" | "updatedAt">) => Promise<void>
  updateCemiterio: (id: string, cemiterio: Partial<Cemiterio>) => Promise<void>
  deleteCemiterio: (id: string) => Promise<void>
  getCemiterioById: (id: string) => Cemiterio | undefined
  fetchCemiterios: () => Promise<void>
  empresas: Empresa[]
  addEmpresa: (empresa: Omit<Empresa, "_id" | "creator" | "createdAt" | "updatedAt">) => Promise<void>
  updateEmpresa: (id: string, empresa: Partial<Empresa>) => Promise<void>
  deleteEmpresa: (id: string) => Promise<void>
  getEmpresaById: (id: string) => Empresa | undefined
  fetchEmpresas: () => Promise<void>
  alvaras: Alvara[]
  addAlvara: (alvara: Omit<Alvara, "_id" | "creator" | "createdAt" | "updatedAt">) => Promise<void>
  updateAlvara: (id: string, alvara: Partial<Alvara>) => Promise<void>
  deleteAlvara: (id: string) => Promise<void>
  getAlvaraById: (id: string) => Alvara | undefined
  fetchAlvaras: () => Promise<void>
  loading: boolean
}

const GuiasContext = createContext<GuiasContextType | undefined>(undefined)

export function GuiasProvider({ children }: { children: React.ReactNode }) {
  const [guias, setGuias] = useState<Guia[]>([])
  const [guiasExcluidas, setGuiasExcluidas] = useState<Guia[]>([])
  const [usuarios, setUsuarios] = useState<Usuario[]>([])
  const [pagamentos, setPagamentos] = useState<Pagamento[]>([])
  const [aforamentos, setAforamentos] = useState<Aforamento[]>([])
  const [feirantes, setFeirantes] = useState<Feirante[]>([])
  const [alertas, setAlertas] = useState<Alerta[]>([])
  const [boletos, setBoletos] = useState<Boleto[]>([])
  const [vendedores, setVendedores] = useState<Vendedor[]>([])
  const [cemiterios, setCemiterios] = useState<Cemiterio[]>([])
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [alvaras, setAlvaras] = useState<Alvara[]>([])
  const [dadosPrefeitura, setDadosPrefeituraState] = useState<DadosPrefeitura | null>(null)
  const [logoUrl, setLogoUrlState] = useState<string>("https://cdn-static-lumi.artvibe.ai/31/31e890ebb4ec15e69b2ee8facc1c3d21.webp")
  const [loading, setLoading] = useState(false)

  // Buscar dados iniciais do MongoDB
  useEffect(() => {
    fetchGuias()
    fetchGuiasExcluidas()
    fetchUsuarios()
    fetchPagamentos()
    fetchAforamentos()
    fetchFeirantes()
    fetchAlertas()
    fetchBoletos()
    fetchVendedores()
    fetchCemiterios()
    fetchEmpresas()
    fetchAlvaras()
    fetchConfig()
  }, [])

  // Atualizar logoUrl quando configurações mudarem
  useEffect(() => {
    if (dadosPrefeitura?.logoUrl) {
      setLogoUrlState(dadosPrefeitura.logoUrl)
    }
  }, [dadosPrefeitura])

  // Remover lógica automática de movimentação de pagamentos vencidos

  const fetchGuias = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.guias.list({
        filter: { deletedAt: { $exists: false } },
        sort: { createdAt: -1 }
      })
      setGuias(list)
    } catch (error) {
      console.error("Erro ao buscar guias:", error)
      toast.error("Erro ao carregar guias")
    } finally {
      setLoading(false)
    }
  }

  const fetchGuiasExcluidas = async () => {
    try {
      const { list } = await lumi.entities.guias.list({
        filter: { deletedAt: { $exists: true } },
        sort: { deletedAt: -1 }
      })
      setGuiasExcluidas(list)
    } catch (error) {
      console.error("Erro ao buscar guias excluídas:", error)
    }
  }

  // Utilitário: garante que uma data YYYY-MM-DD receba T12:00:00.000Z sem duplicar
  const normalizarDataISO = (data: string | undefined): string | undefined => {
    if (!data) return data
    // Extrai apenas a parte YYYY-MM-DD, ignorando qualquer sufixo que já exista
    const soData = data.split("T")[0]
    if (!soData || soData.length !== 10) return data
    return `${soData}T12:00:00.000Z`
  }

  const addGuia = async (novaGuia: Omit<Guia, "_id" | "status" | "creator" | "createdAt" | "updatedAt">) => {
    try {
      const now = new Date().toISOString()
      
      // Normalizar datas — sem risco de duplicar sufixo T12:00:00.000Z
      const dataObitoCorrigida = normalizarDataISO(novaGuia.dataObito)
      const dataSepultamentoCorrigida = normalizarDataISO(novaGuia.dataSepultamento)
      const dataNascimentoCorrigida = normalizarDataISO(novaGuia.dataNascimento)
      
      await lumi.entities.guias.create({
        ...novaGuia,
        dataObito: dataObitoCorrigida,
        dataSepultamento: dataSepultamentoCorrigida,
        dataNascimento: dataNascimentoCorrigida,
        status: "Concluída",
        creator: "system",
        createdAt: now,
        updatedAt: now
      })
      await fetchGuias()
      toast.success("Guia criada com sucesso!")
    } catch (error) {
      console.error("Erro ao criar guia:", error)
      toast.error("Erro ao criar guia")
    }
  }

  const updateGuia = async (id: string, guiaAtualizada: Partial<Guia>) => {
    try {
      const now = new Date().toISOString()
      
      // Normalizar datas — sem risco de duplicar sufixo T12:00:00.000Z
      const dataObitoCorrigida = normalizarDataISO(guiaAtualizada.dataObito)
      const dataSepultamentoCorrigida = normalizarDataISO(guiaAtualizada.dataSepultamento)
      const dataNascimentoCorrigida = normalizarDataISO(guiaAtualizada.dataNascimento)
      
      await lumi.entities.guias.update(id, {
        ...guiaAtualizada,
        dataObito: dataObitoCorrigida,
        dataSepultamento: dataSepultamentoCorrigida,
        dataNascimento: dataNascimentoCorrigida,
        updatedAt: now
      })
      await fetchGuias()
      toast.success("Guia atualizada com sucesso!")
    } catch (error) {
      console.error("Erro ao atualizar guia:", error)
      toast.error("Erro ao atualizar guia")
    }
  }

  const deleteGuia = async (id: string) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.guias.update(id, {
        deletedAt: now,
        updatedAt: now
      })
      await fetchGuias()
      await fetchGuiasExcluidas()
      toast.success("Guia movida para a lixeira!")
    } catch (error) {
      console.error("Erro ao excluir guia:", error)
      toast.error("Erro ao excluir guia")
    }
  }

  const restaurarGuia = async (id: string) => {
    try {
      await lumi.entities.guias.update(id, {
        deletedAt: null,
        updatedAt: new Date().toISOString()
      })
      await fetchGuias()
      await fetchGuiasExcluidas()
      toast.success("Guia restaurada com sucesso!")
    } catch (error) {
      console.error("Erro ao restaurar guia:", error)
      toast.error("Erro ao restaurar guia")
    }
  }

  const deletarPermanentemente = async (id: string) => {
    try {
      await lumi.entities.guias.delete(id)
      await fetchGuiasExcluidas()
      toast.success("Guia excluída permanentemente!")
    } catch (error) {
      console.error("Erro ao excluir permanentemente:", error)
      toast.error("Erro ao excluir guia")
    }
  }

  const getGuiaById = (id: string) => {
    // Busca tanto nas guias ativas quanto nas excluídas (edição via lixeira)
    return guias.find(g => g._id === id) ?? guiasExcluidas.find(g => g._id === id)
  }

  const fetchUsuarios = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.usuarios.list()
      setUsuarios(list)
    } catch (error) {
      console.error("Erro ao buscar usuários:", error)
      toast.error("Erro ao carregar usuários")
    } finally {
      setLoading(false)
    }
  }

  const addUsuario = async (novoUsuario: Omit<Usuario, "_id" | "creator" | "createdAt" | "updatedAt">) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.usuarios.create({
        ...novoUsuario,
        creator: "system",
        createdAt: now,
        updatedAt: now
      })
      await fetchUsuarios()
      toast.success("Usuário criado com sucesso!")
    } catch (error) {
      console.error("Erro ao criar usuário:", error)
      toast.error("Erro ao criar usuário")
    }
  }

  const updateUsuario = async (id: string, usuarioAtualizado: Partial<Usuario>) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.usuarios.update(id, {
        ...usuarioAtualizado,
        updatedAt: now
      })
      await fetchUsuarios()
      toast.success("Usuário atualizado com sucesso!")
    } catch (error) {
      console.error("Erro ao atualizar usuário:", error)
      toast.error("Erro ao atualizar usuário")
    }
  }

  const deleteUsuario = async (id: string) => {
    try {
      await lumi.entities.usuarios.delete(id)
      await fetchUsuarios()
      toast.success("Usuário excluído com sucesso!")
    } catch (error) {
      console.error("Erro ao excluir usuário:", error)
      toast.error("Erro ao excluir usuário")
    }
  }

  const getUsuarioById = (id: string) => {
    return usuarios.find(u => u._id === id)
  }

  const validateLogin = (username: string, password: string) => {
    const user = usuarios.find(u => u.username === username && u.password === password)
    if (user) {
      return { success: true, user }
    }
    return { success: false }
  }

  const fetchConfig = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.configuracoes.list({ limit: 1 })
      if (list && list.length > 0) {
        setDadosPrefeituraState(list[0])
      }
    } catch (error) {
      console.error("Erro ao buscar configurações:", error)
      toast.error("Erro ao carregar configurações")
    } finally {
      setLoading(false)
    }
  }

  const setDadosPrefeitura = async (dados: Partial<DadosPrefeitura>) => {
    try {
      const now = new Date().toISOString()
      if (dadosPrefeitura && dadosPrefeitura._id) {
        await lumi.entities.configuracoes.update(dadosPrefeitura._id, {
          ...dados,
          updatedAt: now
        })
      } else {
        await lumi.entities.configuracoes.create({
          nomePrefeitura: dados.nomePrefeitura || "",
          secretaria: dados.secretaria || "",
          departamento: dados.departamento || "",
          endereco: dados.endereco || "",
          cep: dados.cep || "",
          cnpj: dados.cnpj || "",
          telefone: dados.telefone || "",
          chavePix: dados.chavePix || "",
          logoUrl: dados.logoUrl || "",
          creator: "system",
          createdAt: now,
          updatedAt: now
        })
      }
      await fetchConfig()
      toast.success("Configurações salvas com sucesso!")
    } catch (error) {
      console.error("Erro ao salvar configurações:", error)
      toast.error("Erro ao salvar configurações")
    }
  }

  const setLogoUrl = async (url: string) => {
    try {
      await setDadosPrefeitura({ logoUrl: url })
      setLogoUrlState(url)
    } catch (error) {
      console.error("Erro ao atualizar logo:", error)
    }
  }

  const fetchPagamentos = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.pagamentos.list({
        sort: { createdAt: -1 }
      })
      setPagamentos(list)
    } catch (error) {
      console.error("Erro ao buscar pagamentos:", error)
      toast.error("Erro ao carregar pagamentos")
    } finally {
      setLoading(false)
    }
  }

  const addPagamento = async (novoPagamento: Omit<Pagamento, "_id" | "numeroComprovante" | "dataExpiracao" | "statusPagamento" | "dataPagamento" | "creator" | "createdAt" | "updatedAt">) => {
    try {
      const now = new Date().toISOString()
      const numeroComprovante = `PAG${Date.now().toString().slice(-8)}`
      
      // Calcular data de expiração
      const dataExpiracao = new Date()
      dataExpiracao.setHours(dataExpiracao.getHours() + novoPagamento.horasValidade)
      
      await lumi.entities.pagamentos.create({
        ...novoPagamento,
        numeroComprovante,
        dataExpiracao: dataExpiracao.toISOString(),
        statusPagamento: "pendente",
        creator: "system",
        createdAt: now,
        updatedAt: now
      })
      await fetchPagamentos()
      toast.success("Pagamento gerado com sucesso!")
    } catch (error) {
      console.error("Erro ao criar pagamento:", error)
      toast.error("Erro ao gerar pagamento")
    }
  }

  const marcarComoPago = async (id: string, dataPagamentoRetroativa?: string) => {
    try {
      // PROTEÇÃO 1: Verificar se já está pago no estado local
      const pagamento = pagamentos.find(p => p._id === id)
      if (pagamento?.statusPagamento === "pago") {
        toast.error("🚫 Este PIX já foi pago e não pode ser pago novamente!")
        return
      }
      
      // PROTEÇÃO 2: Buscar do banco antes de atualizar (garantir dados mais recentes)
      const { list: pagamentosAtuais } = await lumi.entities.pagamentos.list({
        filter: { _id: id }
      })
      
      if (pagamentosAtuais.length > 0 && pagamentosAtuais[0].statusPagamento === "pago") {
        toast.error("🚫 Este PIX já foi pago por outro usuário/processo!")
        await fetchPagamentos()
        return
      }
      
      // Usar data retroativa se fornecida, ou data atual
      const dataPagamento = dataPagamentoRetroativa
        ? `${dataPagamentoRetroativa}T12:00:00.000Z`
        : new Date().toISOString()
      
      // UPDATE OTIMISTA: Atualizar estado local IMEDIATAMENTE
      setPagamentos(prevPagamentos => 
        prevPagamentos.map(p => 
          p._id === id 
            ? { ...p, statusPagamento: "pago" as const, dataPagamento }
            : p
        )
      )
      
      const now = new Date().toISOString()
      
      // Tentar atualizar no banco
      await lumi.entities.pagamentos.update(id, {
        statusPagamento: "pago",
        dataPagamento,
        updatedAt: now
      })
      
      // Recarregar do banco para garantir sincronização
      await fetchPagamentos()
      const labelData = dataPagamentoRetroativa
        ? `✅ Pagamento confirmado com data retroativa: ${new Date(dataPagamento).toLocaleDateString("pt-BR")}`
        : "✅ Pagamento confirmado com sucesso!"
      toast.success(labelData)
    } catch (error) {
      console.error("Erro ao atualizar pagamento:", error)
      toast.error("❌ Erro ao marcar pagamento como pago")
      await fetchPagamentos()
    }
  }

  const marcarComoSemComprovante = async (id: string) => {
    try {
      const pagamento = pagamentos.find(p => p._id === id)
      if (pagamento?.statusPagamento === "pago") {
        toast.error("Este pagamento já foi confirmado!")
        return
      }
      
      setPagamentos(prevPagamentos => 
        prevPagamentos.map(p => 
          p._id === id 
            ? { ...p, statusPagamento: "sem_comprovante" as const }
            : p
        )
      )
      
      const now = new Date().toISOString()
      
      await lumi.entities.pagamentos.update(id, {
        statusPagamento: "sem_comprovante",
        updatedAt: now
      })
      
      await fetchPagamentos()
      toast.success("Pagamento marcado como 'Sem Comprovante'")
    } catch (error) {
      console.error("Erro ao atualizar pagamento:", error)
      toast.error("Erro ao marcar como sem comprovante")
      await fetchPagamentos()
    }
  }

  const deletePagamento = async (id: string) => {
    try {
      if (!id) {
        toast.error("ID do pagamento não encontrado")
        return
      }
      await lumi.entities.pagamentos.delete(id)
      await fetchPagamentos()
      toast.success("Pagamento excluído com sucesso!")
    } catch (error: any) {
      console.error("Erro ao excluir pagamento:", error)
      toast.error(error?.message || "Erro ao excluir pagamento")
    }
  }

  const fetchAforamentos = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.aforamentos.list({
        sort: { createdAt: -1 }
      })
      setAforamentos(list)
    } catch (error) {
      console.error("Erro ao buscar aforamentos:", error)
      toast.error("Erro ao carregar aforamentos")
    } finally {
      setLoading(false)
    }
  }

  const addAforamento = async (novoAforamento: Omit<Aforamento, "_id" | "status" | "creator" | "createdAt" | "updatedAt">) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.aforamentos.create({
        ...novoAforamento,
        status: "Concluído",
        creator: "system",
        createdAt: now,
        updatedAt: now
      })
      await fetchAforamentos()
      toast.success("Aforamento criado com sucesso!")
    } catch (error) {
      console.error("Erro ao criar aforamento:", error)
      toast.error("Erro ao criar aforamento")
    }
  }

  const updateAforamento = async (id: string, aforamentoAtualizado: Partial<Aforamento>) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.aforamentos.update(id, {
        ...aforamentoAtualizado,
        updatedAt: now
      })
      await fetchAforamentos()
      toast.success("Aforamento atualizado com sucesso!")
    } catch (error) {
      console.error("Erro ao atualizar aforamento:", error)
      toast.error("Erro ao atualizar aforamento")
    }
  }

  const deleteAforamento = async (id: string) => {
    try {
      await lumi.entities.aforamentos.delete(id)
      await fetchAforamentos()
      toast.success("Aforamento excluído com sucesso!")
    } catch (error) {
      console.error("Erro ao excluir aforamento:", error)
      toast.error("Erro ao excluir aforamento")
    }
  }

  const getAforamentoById = (id: string) => {
    return aforamentos.find(a => a._id === id)
  }

  const fetchFeirantes = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.feirantes.list({
        sort: { createdAt: -1 }
      })
      setFeirantes(list)
    } catch (error) {
      console.error("Erro ao buscar feirantes:", error)
      toast.error("Erro ao carregar feirantes")
    } finally {
      setLoading(false)
    }
  }

  const addFeirante = async (novoFeirante: Omit<Feirante, "_id" | "creator" | "createdAt" | "updatedAt">) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.feirantes.create({
        ...novoFeirante,
        creator: "system",
        createdAt: now,
        updatedAt: now
      })
      await fetchFeirantes()
      toast.success("Feirante cadastrado com sucesso!")
    } catch (error) {
      console.error("Erro ao criar feirante:", error)
      toast.error("Erro ao cadastrar feirante")
    }
  }

  const updateFeirante = async (id: string, feiranteAtualizado: Partial<Feirante>) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.feirantes.update(id, {
        ...feiranteAtualizado,
        updatedAt: now
      })
      await fetchFeirantes()
      toast.success("Feirante atualizado com sucesso!")
    } catch (error) {
      console.error("Erro ao atualizar feirante:", error)
      toast.error("Erro ao atualizar feirante")
    }
  }

  const deleteFeirante = async (id: string) => {
    try {
      await lumi.entities.feirantes.delete(id)
      await fetchFeirantes()
      toast.success("Feirante excluído com sucesso!")
    } catch (error) {
      console.error("Erro ao excluir feirante:", error)
      toast.error("Erro ao excluir feirante")
    }
  }

  const getFeiranteById = (id: string) => {
    return feirantes.find(f => f._id === id)
  }

  const fetchAlertas = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.alertas.list({
        sort: { createdAt: -1 }
      })
      setAlertas(list)
    } catch (error) {
      console.error("Erro ao buscar alertas:", error)
      toast.error("Erro ao carregar alertas")
    } finally {
      setLoading(false)
    }
  }

  const addAlerta = async (novoAlerta: Omit<Alerta, "_id" | "creator" | "createdAt" | "updatedAt">) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.alertas.create({
        ...novoAlerta,
        creator: "system",
        createdAt: now,
        updatedAt: now
      })
      await fetchAlertas()
      toast.success("Alerta registrado no histórico!")
    } catch (error) {
      console.error("Erro ao criar alerta:", error)
      toast.error("Erro ao registrar alerta")
    }
  }

  const fetchBoletos = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.boletos.list({
        sort: { createdAt: -1 }
      })
      setBoletos(list)
    } catch (error) {
      console.error("Erro ao buscar boletos:", error)
      toast.error("Erro ao carregar boletos")
    } finally {
      setLoading(false)
    }
  }

  const addBoleto = async (novoBoleto: Omit<Boleto, "_id" | "numeroBoleto" | "nossoNumero" | "linhaDigitavel" | "codigoBarras" | "statusBoleto" | "creator" | "createdAt" | "updatedAt">) => {
    try {
      const now = new Date().toISOString()
      const numeroBoleto = `BOL${Date.now().toString().slice(-8)}`
      
      await lumi.entities.boletos.create({
        ...novoBoleto,
        numeroBoleto,
        nossoNumero: novoBoleto.nossoNumero || "",
        linhaDigitavel: novoBoleto.linhaDigitavel || "",
        codigoBarras: "",
        statusBoleto: "pendente",
        creator: "system",
        createdAt: now,
        updatedAt: now
      })
      await fetchBoletos()
      toast.success("Boleto cadastrado com sucesso!")
    } catch (error) {
      console.error("Erro ao criar boleto:", error)
      toast.error("Erro ao cadastrar boleto")
    }
  }

  const updateBoleto = async (id: string, boletoAtualizado: Partial<Boleto>) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.boletos.update(id, {
        ...boletoAtualizado,
        updatedAt: now
      })
      await fetchBoletos()
      toast.success("Boleto atualizado com sucesso!")
    } catch (error) {
      console.error("Erro ao atualizar boleto:", error)
      toast.error("Erro ao atualizar boleto")
    }
  }

  const deleteBoleto = async (id: string) => {
    try {
      await lumi.entities.boletos.delete(id)
      await fetchBoletos()
      toast.success("Boleto excluído com sucesso!")
    } catch (error) {
      console.error("Erro ao excluir boleto:", error)
      toast.error("Erro ao excluir boleto")
    }
  }

  const cancelarBoleto = async (id: string) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.boletos.update(id, {
        statusBoleto: "cancelado",
        updatedAt: now
      })
      await fetchBoletos()
      toast.success("Boleto cancelado com sucesso!")
    } catch (error) {
      console.error("Erro ao cancelar boleto:", error)
      toast.error("Erro ao cancelar boleto")
    }
  }

  const marcarBoletoComoPago = async (id: string, valorPago: string) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.boletos.update(id, {
        statusBoleto: "pago",
        dataPagamento: now,
        valorPago,
        updatedAt: now
      })
      await fetchBoletos()
      toast.success("Boleto marcado como pago!")
    } catch (error) {
      console.error("Erro ao marcar boleto como pago:", error)
      toast.error("Erro ao marcar como pago")
    }
  }

  const getBoletoById = (id: string) => {
    return boletos.find(b => b._id === id)
  }

  const fetchVendedores = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.vendedores.list({
        sort: { createdAt: -1 }
      })
      setVendedores(list)
    } catch (error) {
      console.error("Erro ao buscar vendedores:", error)
      toast.error("Erro ao carregar vendedores")
    } finally {
      setLoading(false)
    }
  }

  const addVendedor = async (novoVendedor: Omit<Vendedor, "_id" | "creator" | "createdAt" | "updatedAt">) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.vendedores.create({
        ...novoVendedor,
        creator: "system",
        createdAt: now,
        updatedAt: now
      })
      await fetchVendedores()
      toast.success("Vendedor cadastrado com sucesso!")
    } catch (error) {
      console.error("Erro ao criar vendedor:", error)
      toast.error("Erro ao cadastrar vendedor")
    }
  }

  const updateVendedor = async (id: string, vendedorAtualizado: Partial<Vendedor>) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.vendedores.update(id, {
        ...vendedorAtualizado,
        updatedAt: now
      })
      await fetchVendedores()
      toast.success("Vendedor atualizado com sucesso!")
    } catch (error) {
      console.error("Erro ao atualizar vendedor:", error)
      toast.error("Erro ao atualizar vendedor")
    }
  }

  const deleteVendedor = async (id: string) => {
    try {
      await lumi.entities.vendedores.delete(id)
      await fetchVendedores()
      toast.success("Vendedor excluído com sucesso!")
    } catch (error) {
      console.error("Erro ao excluir vendedor:", error)
      toast.error("Erro ao excluir vendedor")
    }
  }

  const getVendedorById = (id: string) => {
    return vendedores.find(v => v._id === id)
  }

  const fetchCemiterios = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.cemiterios.list({
        sort: { createdAt: -1 }
      })
      setCemiterios(list)
    } catch (error) {
      console.error("Erro ao buscar cemitérios:", error)
      toast.error("Erro ao carregar cemitérios")
    } finally {
      setLoading(false)
    }
  }

  const addCemiterio = async (novoCemiterio: Omit<Cemiterio, "_id" | "creator" | "createdAt" | "updatedAt">) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.cemiterios.create({
        ...novoCemiterio,
        creator: "system",
        createdAt: now,
        updatedAt: now
      })
      await fetchCemiterios()
      toast.success("Cemitério cadastrado com sucesso!")
    } catch (error) {
      console.error("Erro ao criar cemitério:", error)
      toast.error("Erro ao cadastrar cemitério")
    }
  }

  const updateCemiterio = async (id: string, cemiterioAtualizado: Partial<Cemiterio>) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.cemiterios.update(id, {
        ...cemiterioAtualizado,
        updatedAt: now
      })
      await fetchCemiterios()
      toast.success("Cemitério atualizado com sucesso!")
    } catch (error) {
      console.error("Erro ao atualizar cemitério:", error)
      toast.error("Erro ao atualizar cemitério")
    }
  }

  const deleteCemiterio = async (id: string) => {
    try {
      if (!id) {
        toast.error("ID do cemitério não encontrado")
        return
      }
      await lumi.entities.cemiterios.delete(id)
      await fetchCemiterios()
      toast.success("Cemitério excluído com sucesso!")
    } catch (error: any) {
      console.error("Erro ao excluir cemitério:", error)
      toast.error(error?.message || "Erro ao excluir cemitério")
    }
  }

  const getCemiterioById = (id: string) => {
    return cemiterios.find(c => c._id === id)
  }

  const fetchEmpresas = async () => {
    try {
      const { list } = await lumi.entities.empresas.list({ sort: { createdAt: -1 } })
      setEmpresas(list)
    } catch (error) {
      console.error("Erro ao buscar empresas:", error)
    }
  }

  const addEmpresa = async (novaEmpresa: Omit<Empresa, "_id" | "creator" | "createdAt" | "updatedAt">) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.empresas.create({ ...novaEmpresa, creator: "system", createdAt: now, updatedAt: now })
      await fetchEmpresas()
      toast.success("Empresa cadastrada com sucesso!")
    } catch (error) {
      console.error("Erro ao criar empresa:", error)
      toast.error("Erro ao cadastrar empresa")
    }
  }

  const updateEmpresa = async (id: string, empresa: Partial<Empresa>) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.empresas.update(id, { ...empresa, updatedAt: now })
      await fetchEmpresas()
      toast.success("Empresa atualizada com sucesso!")
    } catch (error) {
      console.error("Erro ao atualizar empresa:", error)
      toast.error("Erro ao atualizar empresa")
    }
  }

  const deleteEmpresa = async (id: string) => {
    try {
      await lumi.entities.empresas.delete(id)
      await fetchEmpresas()
      toast.success("Empresa excluída com sucesso!")
    } catch (error: any) {
      console.error("Erro ao excluir empresa:", error)
      toast.error(error?.message || "Erro ao excluir empresa")
    }
  }

  const getEmpresaById = (id: string) => empresas.find(e => e._id === id)

  const fetchAlvaras = async () => {
    try {
      const { list } = await lumi.entities.alvaras.list({ sort: { createdAt: -1 } })
      setAlvaras(list)
    } catch (error) {
      console.error("Erro ao buscar alvarás:", error)
    }
  }

  const addAlvara = async (novoAlvara: Omit<Alvara, "_id" | "creator" | "createdAt" | "updatedAt">) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.alvaras.create({ ...novoAlvara, creator: "system", createdAt: now, updatedAt: now })
      await fetchAlvaras()
      toast.success("Alvará emitido com sucesso!")
    } catch (error) {
      console.error("Erro ao emitir alvará:", error)
      toast.error("Erro ao emitir alvará")
    }
  }

  const updateAlvara = async (id: string, alvara: Partial<Alvara>) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.alvaras.update(id, { ...alvara, updatedAt: now })
      await fetchAlvaras()
      toast.success("Alvará atualizado com sucesso!")
    } catch (error) {
      console.error("Erro ao atualizar alvará:", error)
      toast.error("Erro ao atualizar alvará")
    }
  }

  const deleteAlvara = async (id: string) => {
    try {
      await lumi.entities.alvaras.delete(id)
      await fetchAlvaras()
      toast.success("Alvará excluído com sucesso!")
    } catch (error: any) {
      console.error("Erro ao excluir alvará:", error)
      toast.error(error?.message || "Erro ao excluir alvará")
    }
  }

  const getAlvaraById = (id: string) => alvaras.find(a => a._id === id)

  return (
    <GuiasContext.Provider value={{ 
      guias,
      guiasExcluidas,
      addGuia, 
      updateGuia, 
      deleteGuia,
      restaurarGuia,
      deletarPermanentemente,
      getGuiaById,
      fetchGuias,
      fetchGuiasExcluidas,
      logoUrl,
      setLogoUrl,
      usuarios,
      addUsuario,
      updateUsuario,
      deleteUsuario,
      getUsuarioById,
      fetchUsuarios,
      validateLogin,
      dadosPrefeitura,
      setDadosPrefeitura,
      fetchConfig,
      pagamentos,
      addPagamento,
      marcarComoPago,
      marcarComoSemComprovante,
      deletePagamento,
      fetchPagamentos,
      aforamentos,
      addAforamento,
      updateAforamento,
      deleteAforamento,
      getAforamentoById,
      fetchAforamentos,
      feirantes,
      addFeirante,
      updateFeirante,
      deleteFeirante,
      getFeiranteById,
      fetchFeirantes,
      alertas,
      addAlerta,
      fetchAlertas,
      boletos,
      addBoleto,
      updateBoleto,
      deleteBoleto,
      cancelarBoleto,
      marcarBoletoComoPago,
      getBoletoById,
      fetchBoletos,
      vendedores,
      addVendedor,
      updateVendedor,
      deleteVendedor,
      getVendedorById,
      fetchVendedores,
      cemiterios,
      addCemiterio,
      updateCemiterio,
      deleteCemiterio,
      getCemiterioById,
      fetchCemiterios,
      empresas,
      addEmpresa,
      updateEmpresa,
      deleteEmpresa,
      getEmpresaById,
      fetchEmpresas,
      alvaras,
      addAlvara,
      updateAlvara,
      deleteAlvara,
      getAlvaraById,
      fetchAlvaras,
      loading
    }}>
      {children}
    </GuiasContext.Provider>
  )
}

export function useGuias() {
  const context = useContext(GuiasContext)
  if (!context) {
    throw new Error("useGuias deve ser usado dentro de GuiasProvider")
  }
  return context
}
